import { useEffect, useRef, useState } from "react";

/**
 * HiddenResetButton — Mitarbeiter-Reset, „versteckt sichtbar".
 *
 * Two-Tap-Pattern (iPad-/Touch-tauglich):
 *  - Idle:    8 % Deckkraft, kleiner roter Punkt unten links
 *  - Armed:   nach 1. Tap ~45 % Deckkraft + Pulsieren als Bestätigung
 *  - Confirm: 2. Tap innerhalb 4 s → Reset
 *  - Timeout: nach 4 s ohne 2. Tap → zurück zu Idle
 *
 * Zusätzlich: Strg/Cmd + Alt + R löst sofort den Reset aus.
 */
export const HiddenResetButton = ({ rootPath = "/", storageKeys = [] }) => {
  const [armed, setArmed] = useState(false);
  const timerRef = useRef(null);

  const fullReset = () => {
    try {
      storageKeys.forEach((k) => sessionStorage.removeItem(k));
      sessionStorage.clear();
    } catch {
      /* noop */
    }
    window.location.replace(rootPath);
  };

  const disarm = () => {
    setArmed(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleTap = (e) => {
    e.preventDefault();
    if (armed) {
      disarm();
      fullReset();
    } else {
      setArmed(true);
      timerRef.current = setTimeout(() => setArmed(false), 4000);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      // Strg + Alt + R (oder Cmd + Alt + R auf macOS) → sofortiger Reset
      if ((e.ctrlKey || e.metaKey) && e.altKey && (e.key === "r" || e.key === "R")) {
        e.preventDefault();
        fullReset();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <button
      type="button"
      onClick={handleTap}
      aria-label={armed ? "Reset bestätigen" : "Interner Reset"}
      title={armed ? "Erneut tippen zum Bestätigen" : ""}
      data-testid="hidden-reset"
      data-armed={armed ? "true" : "false"}
      tabIndex={-1}
      className="fixed bottom-2 left-2 z-50 w-11 h-11 rounded-full flex items-center justify-center select-none"
      style={{
        background: "transparent",
        border: "none",
        WebkitTapHighlightColor: "transparent",
        outline: "none",
      }}
    >
      <span
        className={`block rounded-full transition-all duration-300 ${
          armed
            ? "w-3.5 h-3.5 bg-[#d90429] shadow-[0_0_0_4px_rgba(217,4,41,0.18)] animate-pulse"
            : "w-2 h-2 bg-[#d90429]"
        }`}
        style={{ opacity: armed ? 0.45 : 0.08 }}
      />
    </button>
  );
};
